import React, { useState, useEffect, useCallback } from 'react';
import { useModalDismiss } from '../../hooks/useModalDismiss';
import api from '../../Api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner, faCheckCircle, faClock, faUserSlash, faPauseCircle,
  faTimesCircle, faRotateLeft, faSync, faTimes,
  faChevronLeft, faChevronRight, faSearch, faFileExcel
} from '@fortawesome/free-solid-svg-icons';
import SearchableSelect from '../../components/SearchableSelect';

// Control-state buckets, in the order they are shown. `blocking` = not payable as-is.
const BUCKETS = [
  { key: 'validated', label: 'Validé', hint: 'Sera payé', icon: faCheckCircle, blocking: false,
    text: 'text-green-700', value: 'text-green-600', iconColor: 'text-green-500', dot: 'bg-green-500' },
  { key: 'pending_assigned', label: 'En attente (assigné)', hint: 'Un contrôleur l\'a en file', icon: faClock, blocking: true,
    text: 'text-amber-700', value: 'text-amber-600', iconColor: 'text-amber-500', dot: 'bg-amber-500' },
  { key: 'unassigned', label: 'Non assigné', hint: 'Aucune assignation : personne ne le contrôlera', icon: faUserSlash, blocking: true,
    text: 'text-red-700', value: 'text-red-600', iconColor: 'text-red-500', dot: 'bg-red-500' },
  { key: 'hold', label: 'En hold', hint: 'Contrôle non conclu, à trancher', icon: faPauseCircle, blocking: true,
    text: 'text-orange-700', value: 'text-orange-600', iconColor: 'text-orange-500', dot: 'bg-orange-500' },
  { key: 'rejected', label: 'Rejeté', hint: 'Ne sera pas payé', icon: faTimesCircle, blocking: true,
    text: 'text-gray-700', value: 'text-gray-600', iconColor: 'text-gray-500', dot: 'bg-gray-400' },
];

// House panel style, shared with the other control pages.
const PANEL = 'bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20';

const PAGE_SIZE = 20;

const fmtMoney = (n) => `${Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} DH`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '-');

/** Monday of the current week, and the Sunday that closes it, as yyyy-mm-dd. */
const currentWeek = () => {
  const now = new Date();
  const day = (now.getDay() + 6) % 7; // 0 = Monday
  const monday = new Date(now); monday.setDate(now.getDate() - day);
  const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { start: iso(monday), end: iso(sunday) };
};

const F2fPaymentPreview = () => {
  const week = currentWeek();
  const [periodStart, setPeriodStart] = useState(week.start);
  const [periodEnd, setPeriodEnd] = useState(week.end);
  const [surveyId, setSurveyId] = useState(null);
  const [data, setData] = useState(null);
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // "Déjà reporté" rows are paged server-side (deferred_only) so the list is never cut off.
  const [deferredPage, setDeferredPage] = useState(1);
  const [deferredRows, setDeferredRows] = useState(null);
  const [deferredLoading, setDeferredLoading] = useState(false);
  // Its own filters: the section is a worklist, narrowed without touching the page period.
  // `deferredSearchInput` is what is typed, `deferredSearch` the debounced value sent.
  const [deferredSearchInput, setDeferredSearchInput] = useState('');
  const [deferredSearch, setDeferredSearch] = useState('');
  const [deferredBucket, setDeferredBucket] = useState('');
  const [deferredSurveyId, setDeferredSurveyId] = useState(null);
  const deferredFiltered = Boolean(deferredSearch || deferredBucket || deferredSurveyId);

  // Drill-down modal: which submissions sit behind one cell of any breakdown table
  const [drill, setDrill] = useState(null); // { scope, bucket }
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState(null);

  const fetchPreview = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { period_start: periodStart, period_end: periodEnd };
      if (surveyId) params.survey_id = surveyId;
      const res = await api.get('/control/f2f-payment-preview', { params });
      if (res.data.success) {
        setData(res.data.data);
        // Keep the survey list from the unfiltered call so the filter never empties itself.
        if (!surveyId) setSurveys(res.data.data.surveys || []);
      } else {
        setError(res.data.message || 'Erreur lors du chargement');
      }
    } catch (e) {
      console.error('Error loading F2F payment preview:', e);
      setError(e.response?.data?.message || 'Erreur lors du chargement de l\'aperçu');
    } finally {
      setLoading(false);
    }
  }, [periodStart, periodEnd, surveyId]);

  useEffect(() => { fetchPreview(); }, [fetchPreview]);

  // Excel of everything behind "Risque de report", for the period/survey currently shown.
  const [exporting, setExporting] = useState(false);
  const exportBlocked = async () => {
    try {
      setExporting(true);
      const params = { period_start: periodStart, period_end: periodEnd };
      if (surveyId) params.survey_id = surveyId;
      const res = await api.get('/control/f2f-payment-preview/export', {
        params,
        responseType: 'blob',
        timeout: 300000,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const cd = res.headers['content-disposition'];
      const match = cd && cd.match(/filename="?([^"]+)"?/i);
      link.setAttribute('download', match ? match[1] : `risque_de_report_f2f_${periodStart}_${periodEnd}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error exporting blocked submissions:', e);
      setError('Erreur lors de l\'export. Veuillez réessayer.');
    } finally {
      setExporting(false);
    }
  };

  // Debounce the typed term so a request isn't fired on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDeferredSearch(deferredSearchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [deferredSearchInput]);

  const fetchDeferred = useCallback(async () => {
    try {
      setDeferredLoading(true);
      const params = {
        period_start: periodStart,
        period_end: periodEnd,
        deferred_only: 1,
        page: deferredPage,
        per_page: PAGE_SIZE,
      };
      if (deferredSurveyId) params.survey_id = deferredSurveyId;
      if (deferredBucket) params.bucket = deferredBucket;
      if (deferredSearch) params.search = deferredSearch;
      const res = await api.get('/control/f2f-payment-preview/details', { params });
      if (res.data.success) setDeferredRows(res.data.data);
    } catch (e) {
      console.error('Error loading deferred rows:', e);
      setDeferredRows(null);
    } finally {
      setDeferredLoading(false);
    }
  }, [periodStart, periodEnd, deferredSurveyId, deferredBucket, deferredSearch, deferredPage]);

  useEffect(() => { fetchDeferred(); }, [fetchDeferred]);

  // The page-level survey filter seeds this section's own one, so the select always shows
  // the survey actually in effect for this table rather than an inherited-but-hidden value.
  useEffect(() => { setDeferredSurveyId(surveyId); }, [surveyId]);

  // Any filter change changes the list: back to page 1.
  useEffect(() => {
    setDeferredPage(1);
  }, [periodStart, periodEnd, deferredSurveyId, deferredBucket, deferredSearch]);

  /**
   * `scope` = what the clicked cell belongs to: { type, title, subtitle, params }.
   * Its params override the page-level survey filter (a survey row drills into itself).
   */
  const openDrill = async (scope, bucket) => {
    setDrill({ scope, bucket });
    setDrillData(null);
    setDrillError(null);
    setDrillLoading(true);
    try {
      const params = {
        period_start: periodStart,
        period_end: periodEnd,
        bucket: bucket.key,
        ...(surveyId ? { survey_id: surveyId } : {}),
        ...scope.params,
      };
      const res = await api.get('/control/f2f-payment-preview/details', { params });
      if (res.data.success) setDrillData(res.data.data);
      else setDrillError(res.data.message || 'Erreur lors du chargement du détail');
    } catch (e) {
      console.error('Error loading preview details:', e);
      setDrillError(e.response?.data?.message || 'Erreur lors du chargement du détail');
    } finally {
      setDrillLoading(false);
    }
  };

  const drillSupervisor = (row, bucket) => openDrill({
    type: 'supervisor',
    title: row.supervisor_name,
    subtitle: row.supervisor_ref,
    params: { supervisor_id: row.supervisor_id },
  }, bucket);

  const drillSurvey = (row, bucket) => openDrill({
    type: 'survey',
    title: row.survey_title,
    subtitle: row.limesurvey_id ? `ID: ${row.limesurvey_id}` : null,
    params: { survey_id: row.survey_id },
  }, bucket);

  const drillController = (row) => openDrill({
    type: 'controller',
    title: row.controller_name,
    subtitle: row.controller_ref,
    params: { controller_id: row.controller_id },
  }, BUCKETS.find((b) => b.key === 'pending_assigned'));

  const totals = data?.totals;
  const blockedCount = totals?.blocked_count || 0;
  const blockedAmount = totals?.blocked_amount || 0;

  // `onDrill` makes the cell clickable (all breakdown tables use it).
  const bucketCell = (row, b, onDrill) => {
    const count = row?.[b.key]?.count || 0;
    if (!count) return <span className="text-gray-300"> · </span>;

    const content = (
      <div className="flex items-baseline gap-1.5">
        <span className={`font-semibold ${b.text}`}>{count}</span>
        <span className="text-xs text-gray-400">{fmtMoney(row[b.key].amount)}</span>
      </div>
    );

    if (!onDrill) return content;

    return (
      <button
        type="button"
        onClick={() => onDrill(row, b)}
        title="Voir les soumissions concernées"
        className="text-left rounded px-1.5 py-1 -mx-1.5 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-red-400 underline decoration-dotted underline-offset-4 decoration-gray-300"
      >
        {content}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted background, same treatment as the other control pages */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-2">Contrôles à faire avant paie</h1>
          <p className="text-gray-600 text-sm sm:text-base">
            Ce qui serait payé et ce qui risque d'être reporté si le lot de paie était créé maintenant.
            Seule une soumission <strong>validée</strong> est payée ; tout le reste est reporté au lot suivant
            (sauf décision contraire du chef de terrain).
          </p>
        </div>

        {/* Filters */}
        {/* z-20: `backdrop-blur-md` gives every panel its own stacking context, so the
            cards below would paint over the survey dropdown however high its own z-index. */}
        <div className={`${PANEL} p-4 sm:p-6 mb-6 relative z-20`}>
          <div className="flex flex-col lg:flex-row gap-4 lg:items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Du</label>
              <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Au</label>
              <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none text-sm" />
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Enquête F2F</label>
              <SearchableSelect
                options={surveys}
                selectedValue={surveyId}
                onChange={setSurveyId}
                placeholder="Toutes les enquêtes F2F"
                searchPlaceholder="Rechercher par titre ou ID..."
                getOptionLabel={(s) => s.title}
                getOptionMeta={(s) => (s.limesurvey_id ? `ID: ${s.limesurvey_id}` : null)}
                triggerClassName="px-3 py-2"
                textClassName="text-sm"
              />
            </div>
            <button onClick={fetchPreview} disabled={loading}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg flex items-center gap-2 text-sm font-medium">
              <FontAwesomeIcon icon={faSync} className={loading ? 'animate-spin' : ''} />
              Actualiser
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Les soumissions déjà reportées d'une période précédente sont incluses quelle que soit la date choisie.
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 mb-6 text-sm">{error}</div>
        )}

        {loading && !data ? (
          <div className="flex items-center justify-center py-20">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-red-600" />
          </div>
        ) : data && (
          <>
            {/* Headline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className={`${PANEL} p-6`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Payable en l'état</p>
                    <p className="text-2xl font-bold text-green-600">{totals.validated.count} soumission(s)</p>
                    <p className="text-xs text-gray-500 mt-1">{fmtMoney(totals.payable_amount)} validé(s)</p>
                  </div>
                  <FontAwesomeIcon icon={faCheckCircle} className="text-3xl text-green-500" />
                </div>
              </div>
              <div className={`${PANEL} p-6`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Risque de report</p>
                    <p className="text-2xl font-bold text-red-600">{blockedCount} soumission(s)</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {fmtMoney(blockedAmount)} · sur {totals.total_count} dans le vivier
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={exportBlocked}
                    disabled={exporting || blockedCount === 0}
                    title={blockedCount === 0 ? 'Rien à exporter' : `Exporter les ${blockedCount} soumission(s) bloquée(s) en Excel`}
                    className="flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    <FontAwesomeIcon icon={exporting ? faSpinner : faFileExcel} className={`text-2xl ${exporting ? 'animate-spin' : ''}`} />
                    <span className="text-sm font-medium">Excel</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Buckets */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
              {BUCKETS.map((b) => (
                <div key={b.key} className={`${PANEL} p-6`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-600">{b.label}</p>
                      <p className={`text-2xl font-bold ${b.value}`}>{totals[b.key].count}</p>
                      <p className="text-xs text-gray-500">{fmtMoney(totals[b.key].amount)}</p>
                    </div>
                    <FontAwesomeIcon icon={b.icon} className={`text-3xl ${b.iconColor} flex-shrink-0`} />
                  </div>
                  <p className="text-[11px] text-gray-500 mt-3 leading-snug">{b.hint}</p>
                </div>
              ))}
            </div>

            {/* Deferred — top priority */}
            {/* No `overflow-hidden` here: it would clip the survey dropdown. The rounded
                bottom is handled by the results container instead. z-10 keeps that dropdown
                above the breakdown panels below, which each open their own stacking context. */}
            <div className={`${PANEL} mb-6 relative z-10`}>
              <div className="px-5 py-4 border-b border-gray-200 bg-gray-50/70 rounded-t-xl">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <FontAwesomeIcon icon={faRotateLeft} className="text-red-600" />
                    Déjà reporté : priorité absolue
                  </h2>
                  <div className="text-sm text-gray-700">
                    <strong>{data.deferred.count}</strong> soumission(s) · {fmtMoney(data.deferred.blocked_amount)} encore bloqué(s)
                  </div>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Ces personnes attendent leur paiement depuis au moins un lot. Les contrôler en premier.
                </p>
              </div>

              <div className="px-5 py-3 border-b border-gray-200 flex flex-col lg:flex-row gap-3 lg:items-end">
                <div className="flex-1 min-w-0">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Rechercher</label>
                  <div className="relative">
                    <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                    <input
                      type="text"
                      value={deferredSearchInput}
                      onChange={(e) => setDeferredSearchInput(e.target.value)}
                      placeholder="ID soumission, réf., nom d'enquêteur ou de prestataire..."
                      className="w-full pl-9 pr-9 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none text-sm"
                    />
                    {deferredSearchInput && (
                      <button
                        type="button"
                        onClick={() => setDeferredSearchInput('')}
                        title="Effacer la recherche"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 px-1"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="lg:w-56">
                  <label className="block text-xs font-medium text-gray-700 mb-1">État</label>
                  <select
                    value={deferredBucket}
                    onChange={(e) => setDeferredBucket(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none text-sm bg-white"
                  >
                    <option value="">Tous les états</option>
                    {BUCKETS.map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
                  </select>
                </div>
                <div className="lg:w-72">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Enquête</label>
                  <SearchableSelect
                    options={surveys}
                    selectedValue={deferredSurveyId}
                    onChange={setDeferredSurveyId}
                    placeholder="Toutes les enquêtes"
                    searchPlaceholder="Rechercher par titre ou ID..."
                    getOptionLabel={(s) => s.title}
                    getOptionMeta={(s) => (s.limesurvey_id ? `ID: ${s.limesurvey_id}` : null)}
                    triggerClassName="px-3 py-2"
                    textClassName="text-sm"
                  />
                </div>
              </div>

              {deferredLoading && !deferredRows ? (
                <div className="px-5 py-10 text-center rounded-b-xl">
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl text-red-600" />
                </div>
              ) : !deferredRows || deferredRows.items.length === 0 ? (
                <div className="px-5 py-8 text-center text-gray-500 text-sm rounded-b-xl">
                  {deferredFiltered ? 'Aucun résultat pour ces filtres.' : 'Aucune soumission reportée.'}
                </div>
              ) : (
                <div className={`rounded-b-xl overflow-hidden ${deferredLoading ? 'opacity-50 transition-opacity' : ''}`}>
                  <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        {['Lots attendus', 'ID Soumission', 'Réf.', 'Enquête', 'Personne', 'Prestataire', 'État', 'Contrôleur', 'Soumis le', 'Contrôlé le', 'Montant'].map((h) => (
                          <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {deferredRows.items.map((it) => {
                        const b = BUCKETS.find((x) => x.key === it.bucket);
                        return (
                          <tr key={it.submission_id} className="hover:bg-gray-50">
                            <td className="px-4 py-2 whitespace-nowrap">
                              <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold text-xs">
                                ×{it.deferred_count}
                              </span>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap font-mono text-gray-900">{it.response_id || `#${it.submission_id}`}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-gray-600">{it.ref || '-'}</td>
                            <td className="px-4 py-2 text-gray-900">{it.survey_title}</td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <div className="text-gray-900">{it.person_name || '-'}</div>
                              <div className="text-xs text-gray-400">{it.person_ref || ''}</div>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-gray-600">{it.supervisor_name || '-'}</td>
                            <td className="px-4 py-2 whitespace-nowrap">
                              <span className={`inline-flex items-center gap-1.5 ${b?.text || 'text-gray-600'}`}>
                                <span className={`w-2 h-2 rounded-full ${b?.dot || 'bg-gray-400'}`} />
                                {b?.label || it.bucket}
                              </span>
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-gray-600">{it.controller_name || '-'}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-gray-500">{fmtDate(it.submitted_at)}</td>
                            {/* Empty, not a dash: nothing here means no controller has ruled on it yet. */}
                            <td className="px-4 py-2 whitespace-nowrap text-gray-500">
                              {it.controlled_at ? fmtDate(it.controlled_at) : ''}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-gray-900 font-medium">{fmtMoney(it.amount)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                  <Pager
                    page={deferredRows.pagination.page}
                    total={deferredRows.pagination.total}
                    onChange={setDeferredPage}
                    loading={deferredLoading}
                  />
                </div>
              )}
            </div>

            {/* Per survey */}
            <BreakdownTable
              title="Par enquête"
              subtitle="Trié par montant bloqué : commencer par le haut débloque le plus d'argent. Cliquez sur un chiffre pour voir les soumissions concernées."
              onDrill={drillSurvey}
              rows={data.by_survey}
              rowKey={(r) => r.survey_id}
              firstHeader="Enquête"
              renderFirst={(r) => (
                <>
                  <div className="text-gray-900 font-medium">{r.survey_title}</div>
                  <div className="text-xs text-gray-400">
                    {r.limesurvey_id ? `ID: ${r.limesurvey_id} · ` : ''}{fmtMoney(r.price)} / complète
                  </div>
                </>
              )}
              bucketCell={bucketCell}
            />

            {/* Per supervisor */}
            <BreakdownTable
              title="Par prestataire"
              subtitle="Le travail des enquêteurs est rattaché à leur prestataire, comme sur la ligne de paie. Cliquez sur un chiffre pour voir les soumissions concernées."
              onDrill={drillSupervisor}
              rows={data.by_supervisor}
              rowKey={(r) => r.supervisor_id}
              firstHeader="Prestataire"
              renderFirst={(r) => (
                <>
                  <div className="text-gray-900 font-medium">{r.supervisor_name}</div>
                  <div className="text-xs text-gray-400">{r.supervisor_ref || ''}</div>
                </>
              )}
              bucketCell={bucketCell}
            />

            {/* Per controller */}
            <ControllerTable rows={data.by_controller} onDrill={drillController} />

            <p className="text-xs text-gray-400 text-center pb-4">
              Aperçu calculé le {data.generated_at ? new Date(data.generated_at).toLocaleString('fr-FR') : '-'}.
              Aucune donnée de paie n'est modifiée par cette page.
            </p>
          </>
        )}
      </div>

      {drill && (
        <DrillModal
          scope={drill.scope}
          bucket={drill.bucket}
          data={drillData}
          loading={drillLoading}
          error={drillError}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
};

/** Controllers sitting on never-controlled work, 20 per page. */
const ControllerTable = ({ rows, onDrill }) => {
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [rows]);
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className={`${PANEL} mb-6 overflow-hidden`}>
      <div className="px-5 py-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900">Par contrôleur : à relancer</h2>
        <p className="text-xs text-gray-500 mt-1">
          Soumissions jamais contrôlées déjà assignées : ces contrôleurs bloquent ce montant.
          Cliquez sur une ligne pour voir les soumissions concernées.
        </p>
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-8 text-center text-gray-500 text-sm">Aucune assignation en attente.</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Contrôleur</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">En attente</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Montant bloqué</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {pageRows.map((c) => (
                  <tr
                    key={c.controller_id}
                    onClick={() => onDrill(c)}
                    title="Voir les soumissions concernées"
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-2">
                      <div className="text-gray-900 font-medium">{c.controller_name}</div>
                      <div className="text-xs text-gray-400">{c.controller_ref || ''}</div>
                    </td>
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onDrill(c); }}
                        className="font-semibold text-amber-700 rounded px-1.5 py-1 -mx-1.5 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-red-400 underline decoration-dotted underline-offset-4 decoration-gray-300"
                      >
                        {c.count}
                      </button>
                    </td>
                    <td className="px-4 py-2 text-gray-900 font-medium">{fmtMoney(c.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} total={rows.length} onChange={setPage} />
        </>
      )}
    </div>
  );
};

/** Which submissions are behind one cell of a breakdown table. */
const DrillModal = ({ scope, bucket, data, loading, error, onClose }) => {
  const dismiss = useModalDismiss(onClose);
  // Columns that would just repeat the scope are dropped (a supervisor drill-down doesn't
  // need a "Prestataire" column, a survey drill-down doesn't need "Enquête", etc.).
  const showSurvey = scope.type !== 'survey';
  const showSupervisor = scope.type !== 'supervisor';
  const showController = scope.type !== 'controller';

  const headers = [
    'ID Soumission', 'Réf.',
    ...(showSurvey ? ['Enquête'] : []),
    'Personne',
    ...(showSupervisor ? ['Prestataire'] : []),
    ...(showController ? ['Contrôleur'] : []),
    'Soumis le', 'Contrôlé le', 'Reports', 'Montant',
  ];

  return (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" {...dismiss}>
    <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
      <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <FontAwesomeIcon icon={bucket.icon} className={bucket.text} />
            {bucket.label}
          </h3>
          <p className="text-sm text-gray-600 mt-0.5">
            {scope.title}
            {scope.subtitle ? ` · ${scope.subtitle}` : ''}
            {data ? ` · ${data.count} soumission(s) · ${fmtMoney(data.amount)}` : ''}
          </p>
          <p className="text-xs text-gray-500 mt-1">{bucket.hint}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" title="Fermer">
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div className="overflow-auto flex-1">
        {loading ? (
          <div className="py-16 text-center">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-red-600" />
          </div>
        ) : error ? (
          <div className="m-5 bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>
        ) : !data || data.items.length === 0 ? (
          <div className="py-16 text-center text-gray-500 text-sm">Aucune soumission.</div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                {headers.map((h) => (
                  <th key={h} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {data.items.map((it) => (
                <tr key={it.submission_id} className="hover:bg-gray-50">
                  <td className="px-4 py-2 whitespace-nowrap font-mono text-gray-900">{it.response_id || `#${it.submission_id}`}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-600">{it.ref || '-'}</td>
                  {showSurvey && (
                    <td className="px-4 py-2 text-gray-900">
                      {it.survey_title}
                      {it.limesurvey_id && <span className="block text-xs text-gray-400">ID: {it.limesurvey_id}</span>}
                    </td>
                  )}
                  <td className="px-4 py-2 whitespace-nowrap">
                    <div className="text-gray-900">{it.person_name || '-'}</div>
                    <div className="text-xs text-gray-400">
                      {it.person_ref ? `${it.person_ref} · ` : ''}
                      {it.person_type === 'supervisor' ? 'Prestataire' : 'Enquêteur'}
                    </div>
                  </td>
                  {showSupervisor && (
                    <td className="px-4 py-2 whitespace-nowrap text-gray-600">{it.supervisor_name || '-'}</td>
                  )}
                  {showController && (
                    <td className="px-4 py-2 whitespace-nowrap text-gray-600">
                      {it.controller_name || '-'}
                      {it.assignment_status && <span className="block text-xs text-gray-400">{it.assignment_status}</span>}
                    </td>
                  )}
                  <td className="px-4 py-2 whitespace-nowrap text-gray-500">{fmtDate(it.submitted_at)}</td>
                  {/* Empty, not a dash: nothing here means no controller has ruled on it yet. */}
                  <td className="px-4 py-2 whitespace-nowrap text-gray-500">
                    {it.controlled_at ? fmtDate(it.controlled_at) : ''}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    {it.deferred_count > 0 ? (
                      <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-semibold text-xs">×{it.deferred_count}</span>
                    ) : (
                      <span className="text-gray-300"> · </span>
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-900 font-medium">{fmtMoney(it.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-end">
        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
          Fermer
        </button>
      </div>
    </div>
  </div>
  );
};

/**
 * Pager shared by every section. `total` is the full row count, never the page's —
 * the figures above the tables stay global.
 */
const Pager = ({ page, total, perPage = PAGE_SIZE, onChange, loading = false }) => {
  const lastPage = Math.max(1, Math.ceil(total / perPage));
  if (total === 0) return null;

  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);

  return (
    <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
      <span className="text-xs text-gray-600">
        {from}–{to} sur <strong>{total}</strong> ligne(s)
      </span>
      {lastPage > 1 && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => onChange(page - 1)}
            className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Page précédente"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <span className="text-xs text-gray-600">Page {page} / {lastPage}</span>
          <button
            type="button"
            disabled={page >= lastPage || loading}
            onClick={() => onChange(page + 1)}
            className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            title="Page suivante"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}
    </div>
  );
};

/** Shared bucket-per-row table used for the survey and supervisor breakdowns. */
const BreakdownTable = ({ title, subtitle, rows, rowKey, firstHeader, renderFirst, bucketCell, onDrill }) => {
  const [page, setPage] = useState(1);

  // A new period / survey filter reshuffles the rows: go back to the first page.
  useEffect(() => { setPage(1); }, [rows]);

  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className={`${PANEL} mb-6 overflow-hidden`}>
      <div className="px-5 py-4 border-b border-gray-200">
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-8 text-center text-gray-500 text-sm">Rien à afficher pour cette période.</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{firstHeader}</th>
                  {BUCKETS.map((b) => (
                    <th key={b.key} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {b.label}
                    </th>
                  ))}
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Risque de report
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {pageRows.map((r) => (
                  <tr key={rowKey(r)} className="hover:bg-gray-50">
                    <td className="px-4 py-3 min-w-[220px]">{renderFirst(r)}</td>
                    {BUCKETS.map((b) => (
                      <td key={b.key} className="px-4 py-3 whitespace-nowrap">{bucketCell(r, b, onDrill)}</td>
                    ))}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="font-bold text-red-700">{fmtMoney(r.blocked_amount)}</div>
                      <div className="text-xs text-gray-400">{r.blocked_count} soumission(s)</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} total={rows.length} onChange={setPage} />
        </>
      )}
    </div>
  );
};

export default F2fPaymentPreview;
